import { Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useWindowSize } from 'react-use';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  ListRowProps,
} from 'react-virtualized';
import 'react-virtualized/styles.css';
import OnboardListItem, { OnboardListItemParams } from './OnboardListItem';

type Props = {
  items: OnboardListItemParams[];
};

/**
 * Renders a virtualized list of onboard list items.
 *
 * @param items - The items to be rendered in the list.
 */
export const OnboardVirtualizedList: React.FC<Props> = ({ items }) => {
  const { height, width } = useWindowSize();

  const cache = useMemo(
    () =>
      new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 100,
      }),
    []
  );

  // clear cache when items change or when something that impacts the size of the list items changes such as height, or width
  useEffect(() => {
    // Clear the cache when items change
    cache.clearAll();
  }, [items, height, width, cache]);

  const renderRow = useCallback(
    ({ index, key, style, parent }: ListRowProps) => (
      <CellMeasurer
        key={key}
        cache={cache}
        parent={parent}
        columnIndex={0}
        rowIndex={index}
      >
        <div style={style}>
          <OnboardListItem onboardListItemParams={items[index]} />
        </div>
      </CellMeasurer>
    ),
    [items, cache]
  );

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
      }}
    >
      <AutoSizer>
        {({ height, width }) => (
          <List
            height={height}
            rowCount={items.length}
            rowHeight={cache.rowHeight}
            rowRenderer={renderRow}
            width={width}
          />
        )}
      </AutoSizer>
    </Box>
  );
};
